<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRoute } from 'vue-router'

import logoUrl from '@/assets/logo/lexentiqa-gold.svg'

import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useNavigationStore } from '@/stores/navigation.store'
import { useBreakpoint } from '@/composables/useBreakpoint'

const { t } = useI18n()

const route = useRoute()
const ui = useUIStore()
const auth = useAuthStore()
const nav = useNavigationStore()
const { isLaptop } = useBreakpoint()

const sidebarRef = ref<HTMLElement | null>(null)

/* Active nav key — falls back to route name when meta.nav isn't set. */
const activeKey = computed(() => route.meta?.nav || route.name)

/* Sidebar nav tree comes from the store — live: GET /navigation
 * via navigationService. Today the mock branch resolves
 * navigation.json with simulated latency. */
const navigation = computed(() => ({
  workspace: nav.workspace,
  tools: nav.tools,
}))

/**
 * Nav labels come from the catalogue keyed by the item's stable `key`, not
 * from the `label` the fixture (later, the API) sends: a server-side string
 * cannot follow the user's chosen language. Any key the catalogue does not
 * know falls back to whatever the source provided, so a new nav entry appears
 * in English rather than as a raw key.
 */
function navLabel(item: { key: string; label?: string }): string {
  const key = `nav.${item.key}`
  const translated = t(key)
  return translated === key ? (item.label ?? '') : translated
}
onMounted(() => nav.fetch())

/* ────────── Laptop-range collapse (1025–1512px) ──────────
 * Port of the hover-to-peek + click-to-pin logic from
 * html_mockup/assets/js/app.js §5. The CSS still owns the visual
 * collapsed/expanded states; this just toggles `is-expanded`.
 */
let collapseTimer: ReturnType<typeof setTimeout> | null = null
const cancelTimer = () => {
  if (collapseTimer) {
    clearTimeout(collapseTimer)
    collapseTimer = null
  }
}
const expand = () => {
  if (!isLaptop.value) return
  cancelTimer()
  ui.setSidebarExpanded(true)
}
const deferredCollapse = (ms = 180) => {
  if (!isLaptop.value || ui.sidebarPinned) return
  cancelTimer()
  collapseTimer = setTimeout(() => {
    collapseTimer = null
    if (!ui.sidebarPinned) ui.setSidebarExpanded(false)
  }, ms)
}

const onSidebarClick = (e: MouseEvent) => {
  if (!isLaptop.value) return
  const target = e.target as HTMLElement
  if (target.closest('[data-action="close-sidebar"]')) return
  if (target.closest('a, button')) return
  // Click on empty rail toggles pin.
  ui.setSidebarPinned(!ui.sidebarPinned)
  if (ui.sidebarPinned) expand()
  else ui.setSidebarExpanded(false)
}

const onDocClick = (e: MouseEvent) => {
  if (!isLaptop.value) return
  const target = e.target as HTMLElement
  if (sidebarRef.value && sidebarRef.value.contains(target)) return
  if (target.closest('[data-action="open-sidebar"]')) return
  ui.setSidebarPinned(false)
  ui.setSidebarExpanded(false)
}

const onKey = (e) => {
  if (e.key === 'Escape' && isLaptop.value) {
    ui.setSidebarPinned(false)
    ui.setSidebarExpanded(false)
  }
}

onMounted(() => {
  document.addEventListener('click', onDocClick)
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('keydown', onKey)
  cancelTimer()
})
</script>

<template>
  <aside
    class="app__sidebar"
    :class="{ 'is-open': ui.sidebarOpen, 'is-expanded': ui.sidebarExpanded }"
  >
    <nav
      ref="sidebarRef"
      class="sidebar"
      aria-label="Primary"
      @mouseenter="expand"
      @mouseleave="deferredCollapse()"
      @click="onSidebarClick"
    >
      <button
        class="sidebar__close"
        data-action="close-sidebar"
        :aria-label="t('topbar.closeMenu')"
        @click="ui.closeDrawers()"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <div class="sidebar__brand">
        <span class="sidebar__brand-mini" aria-hidden="true">L</span>
        <img class="sidebar__logo" :src="logoUrl" alt="Lexentiqa" />
      </div>

      <div class="sidebar__eyebrow">{{ t('nav.workspace') }}</div>
      <div class="nav">
        <RouterLink
          v-for="item in navigation.workspace"
          :key="item.key"
          :to="item.to"
          class="nav-item"
          :class="{ 'is-active': activeKey === item.key }"
          :data-tooltip="item.count != null ? `${navLabel(item)} · ${item.count}` : navLabel(item)"
        >
          <i class="nav-item__icon" :class="item.icon"></i>
          <span class="nav-item__label">{{ navLabel(item) }}</span>
          <span v-if="item.count != null" class="nav-item__count">{{ item.count }}</span>
        </RouterLink>
      </div>

      <div class="sidebar__eyebrow">{{ t('nav.workspaceTools') }}</div>
      <div class="nav">
        <RouterLink
          v-for="item in navigation.tools"
          :key="item.key"
          :to="item.to"
          class="nav-item"
          :class="{ 'is-active': activeKey === item.key }"
          :data-tooltip="item.count != null ? `${navLabel(item)} · ${item.count}` : navLabel(item)"
        >
          <i class="nav-item__icon" :class="item.icon"></i>
          <span class="nav-item__label">{{ navLabel(item) }}</span>
          <span v-if="item.count != null" class="nav-item__count">{{ item.count }}</span>
        </RouterLink>
      </div>

      <div class="sidebar__footer">
        <div class="sidebar__avatar">{{ auth.user.initials }}</div>
        <div>
          <div class="sidebar__user-name">{{ auth.user.name }}</div>
          <div class="sidebar__user-role">{{ auth.user.role }}</div>
        </div>
      </div>
    </nav>
  </aside>
</template>
